
import { Link } from "react-router-dom"

import {
  HeartPulse,
  Baby,
  Heart,
  Users,
  TrendingUp,
  Calendar,
  ArrowRight,
  Stethoscope,
  Activity,
  Shield,
  Target,
  CheckCircle,
  Award,
  MapPin,
  Home,
  Sparkles,
} from "lucide-react"
import SEO from "../SEO"

import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"

export default function PranaamGlancePage() {
  const pranaamGlanceSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaam.pranaamhospitals.in/",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad delivering compassionate, advanced, and patient-centric healthcare.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "telephone": "+91-99660-60000",
      "availableLanguage": ["English", "Telugu", "Hindi"]
    },
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      "https://www.linkedin.com/company/pranaamhospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/about/pranaamGlance"
    },
    "headline": "Pranaam Hospitals at a Glance",
    "description": "An overview of Pranaam Hospitals highlighting its medical expertise, infrastructure, specialties, patient care standards, and commitment to healthcare excellence in Hyderabad.",
    "image": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/pranaam-hospitals-building.jpg",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "Pranaam Hospitals overview",
      "Pranaam Hospitals at a glance",
      "best hospital in Hyderabad",
      "multi specialty hospital Hyderabad",
      "NABH accredited hospital Hyderabad"
    ]
  }
];

  const stats = [
    { label: "Patients Treated", value: "3+ Lacs", icon: Users },
    { label: "Surgeries Performed", value: "50,000+", icon: Activity },
    { label: "Hospital Beds", value: "150", icon: HeartPulse },
    { label: "Years of Excellence", value: "20+", icon: Award },
  ]

  const entities = [
    {
      title: "Pranaam Hospitals",
      icon: HeartPulse,
      established: "2004",
      description:
        "Pranaam Hospitals was the first formal healthcare institution of the Pranaam group with the flagship hospital located at Madinaguda, Hyderabad. Established in 2004, Pranaam Hospitals started with a bed capacity of 10 and over the years has grown to a 150 beds multispeciality hospital. We have now treated over 3 lac patients and conducted over 50,000 surgeries.",
      highlights: [
        "150 bed multispeciality hospital",
        "3+ lacs patients treated",
        "50,000+ surgeries performed",
        "Located at Madinaguda, Hyderabad",
      ],
      image: "/images/PranaamHospitals.png",
      link: "/about",
    },
    {
      title: "Maternity Matters",
      icon: Baby,
      established: "Specialized Care",
      description:
        "Whether you come to Pranaam for a natural birth or a high risk delivery our Maternity Matters centres provide private labour and delivery suites equipped with latest technology and amenities for a safe, comfortable delivery. The Maternity Matters care team works with you to create a customised birth plans thereby ensuring the birth experience is safe and fulfilling. We have the most experienced obstetricians & neonatal specialists in Hyderabad to care for you round the clock.",
      highlights: [
        "Private labour and delivery suites",
        "Latest technology and amenities",
        "Customized birth plans",
        "Experienced obstetricians & neonatal specialists",
      ],
      image: "/images/MaternityMatters.png",
      link: "/maternity-matters",
    },
    {
      title: "Pranaam Healthcare & Lifestyle",
      icon: Heart,
      established: "Wellness Focus",
      description:
        "Pranaam Healthcare & Lifestyle is focussed on wellness, transformational healthcare and community programs through providing care through its hospital, community health centres & long-term care at home. At the core of the organisation is having an innovative approach to manage health outcomes through improving access to quality healthcare using technology and physical access centres.",
      highlights: [
        "Wellness and transformational healthcare",
        "Community health centres",
        "Long-term care at home",
        "Technology-driven access to quality healthcare",
      ],
      image: "/images/PranaamHealthcare&Lifestyle.png",
      link: "/about/history",
    },
  ]

  const objectives = [
    {
      title: "Innovation",
      description: "Finding innovative ways to respond to changing demographics and medical needs",
      icon: Sparkles,
    },
    {
      title: "Efficiency",
      description: "Implementing efficient healthcare delivery systems for optimal patient outcomes",
      icon: TrendingUp,
    },
    {
      title: "Cost-Effectiveness",
      description: "Providing quality healthcare that is accessible and affordable for all",
      icon: Shield,
    },
    {
      title: "Research Excellence",
      description: "Investing in best-in-class research to advance medical knowledge",
      icon: Target,
    },
  ]

  return (
    <>
    <SEO
  title="Pranaam Hospitals at a Glance | Excellence in Healthcare Hyderabad"
  description="Get a quick overview of Pranaam Hospitals at a glance — a NABH-accredited multi-specialty hospital in Hyderabad known for advanced care, expert doctors, and patient-first values."
  canonical="https://pranaam.pranaamhospitals.in/about/pranaamGlance"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Pranaam Hospitals at a glance, Pranaam Hospitals overview,
            multi specialty hospital Hyderabad, NABH hospital Hyderabad,
            best hospital in Hyderabad, Pranaam Hospitals healthcare excellence"
  schema={pranaamGlanceSchema}
/>



    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-primary/10 to-primary/5">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Our Healthcare Ecosystem</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Pranaam at a <span className="text-primary">Glance</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700 max-w-3xl mx-auto">
                Pranaam is a leading healthcare group headquartered out of Hyderabad serving patients across the full
                spectrum of care, spanning primary care, secondary and community care services and long-term care,
                whilst investing in best in-class research.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {stats.map((stat, index) => {
                  const Icon = stat.icon
                  return (
                    <Badge
                      key={index}
                      variant="outline"
                      className="px-4 py-2 border-primary/20 bg-white hover:bg-primary/5 transition-colors"
                    >
                      <Icon className="w-4 h-4 mr-2 text-primary" />
                      <span className="font-bold text-primary mr-1">{stat.value}</span>
                      <span className="text-gray-700">{stat.label}</span>
                    </Badge>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto">
              <Card className="border-2 border-primary/10 bg-gradient-to-br from-gray-50 to-white">
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Target className="w-8 h-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl md:text-3xl font-bold mb-4">Our Primary Objective</h2>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        The primary objective of Pranaam is to find innovative, efficient and cost-effective ways of
                        responding to the changing demographics and medical needs of our patients.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Core Objectives Grid */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-2">Our Approach</Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Driven by <span className="text-primary">Excellence</span>
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  Our approach to healthcare is built on four fundamental pillars that guide everything we do
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {objectives.map((objective, index) => {
                  const Icon = objective.icon
                  return (
                    <Card key={index} className="hover:shadow-lg transition-shadow group">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary transition-colors">
                            <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-xl mb-2 group-hover:text-primary transition-colors">
                              {objective.title}
                            </h3>
                            <p className="text-gray-600">{objective.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Entities Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-2">Our Network</Badge>
                <h2 className="text-3xl font-bold mb-4">
                  The Pranaam <span className="text-primary">Group</span>
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  The Pranaam group comprises specialized entities, each focused on delivering excellence in their
                  respective domains of healthcare
                </p>
              </div>

              <div className="space-y-12">
                {entities.map((entity, index) => {
                  const Icon = entity.icon
                  const isEven = index % 2 === 0

                  return (
                    <Card
                      key={index}
                      className="overflow-hidden hover:shadow-xl transition-all group border-2 border-primary/10"
                    >
                      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${isEven ? "" : "lg:grid-flow-dense"}`}>
                        {/* Image Section */}
                        <div className={`relative h-64 lg:h-auto ${isEven ? "" : "lg:col-start-2"}`}>
                          <img
                            src={entity.image || "/placeholder.svg"}
                            alt={entity.title}
                            fill
                            className="object-cover"
                          />
                          
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:hidden"></div>
                          <Badge className="absolute top-4 left-4 bg-primary text-white">{entity.established}</Badge>
                        </div>

                        {/* Content Section */}
                        <div className={`p-8 lg:p-10 ${isEven ? "" : "lg:col-start-1 lg:row-start-1"}`}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 rounded-lg bg-primary/10">
                              <Icon className="w-8 h-8 text-primary" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold group-hover:text-primary transition-colors">
                                {entity.title}
                              </h3>
                            </div>
                          </div>

                          <p className="text-gray-700 mb-6 leading-relaxed">{entity.description}</p>

                          <div className="space-y-3 mb-6">
                            {entity.highlights.map((highlight, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-gray-700">{highlight}</span>
                              </div>
                            ))}
                          </div>

                          <Button className="group/btn" asChild>
                            <Link to={entity.link}>
                              Learn More
                              <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Area */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <Badge className="mb-4">Our Reach</Badge>
                  <h2 className="text-3xl font-bold mb-6">
                    Comprehensive <span className="text-primary">Healthcare Spectrum</span>
                  </h2>
                  <p className="text-gray-700 mb-6">
                    We serve patients across the full spectrum of care, providing integrated healthcare solutions from
                    primary to tertiary care.
                  </p>

                  <div className="space-y-4">
                    <Card className="border-l-4 border-l-primary">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Stethoscope className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <div>
                            <h4 className="font-bold mb-1">Primary Care</h4>
                            <p className="text-sm text-gray-600">
                              Essential healthcare services for routine medical needs
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-primary">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <HeartPulse className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <div>
                            <h4 className="font-bold mb-1">Secondary & Community Care</h4>
                            <p className="text-sm text-gray-600">
                              Specialized medical services and community health programs
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-primary">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Home className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <div>
                            <h4 className="font-bold mb-1">Long-Term Care</h4>
                            <p className="text-sm text-gray-600">Comprehensive long-term care services at home</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div className="relative h-96 lg:h-[600px] rounded-lg overflow-hidden shadow-xl">
                  <img
                    src="/images/HealthcareSpectrum.png"
                    alt="Healthcare Spectrum"
                    fill
                    className="object-cover"
                  />
                 
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Research & Innovation */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-5xl mx-auto">
              <Card className="bg-gradient-to-br from-primary/5 to-white border-2 border-primary/10">
                <CardContent className="p-8 md:p-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div>
                      <Badge className="mb-4">Innovation & Research</Badge>
                      <h2 className="text-3xl font-bold mb-4">
                        Investing in <span className="text-primary">Best-in-Class Research</span>
                      </h2>
                      <p className="text-gray-700 mb-6">
                        At Pranaam, we believe that advancing medical knowledge through research is crucial to improving
                        patient outcomes. We continuously invest in cutting-edge research initiatives to stay at the
                        forefront of medical innovation.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary" />
                          <span className="text-sm text-gray-700">Clinical research programs</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary" />
                          <span className="text-sm text-gray-700">Collaboration with medical institutions</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary" />
                          <span className="text-sm text-gray-700">Evidence-based medical practices</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-primary" />
                          <span className="text-sm text-gray-700">Continuous medical education</span>
                        </div>
                      </div>
                    </div>

                    <div className="relative h-64 md:h-80 rounded-lg overflow-hidden shadow-lg">
                      <img
                        src="/images/ResearchExcellence.png"
                        alt="Research Excellence"
                        fill
                        className="object-cover"
                      />
                      
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Locations Map */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-2">Our Presence</Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Locations Across <span className="text-primary">Hyderabad</span>
                </h2>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  With multiple locations across Hyderabad, we bring quality healthcare closer to you
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {["Madinaguda", "Nallagandla", "Isnapur", "Zaheerabad"].map((location, index) => (
                  <Card key={index} className="text-center hover:shadow-lg transition-shadow group">
                    <CardContent className="p-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 group-hover:bg-primary mb-4 transition-colors">
                        <MapPin className="w-8 h-8 text-primary group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">{location}</h3>
                      <Button variant="ghost" size="sm" className="mt-2" asChild>
                        <Link to={`/locations/${location.toLowerCase()}`}>
                          View Details
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <Card class="max-w-4xl mx-auto bg-gradient-to-r from-primary to-primary/90 rounded-lg shadow-xl md:p-12 text-white text-center">
              <CardContent className="p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Experience the Pranaam Difference</h2>
                <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                  Join thousands of patients who trust Pranaam for comprehensive, compassionate, and innovative
                  healthcare across all stages of life.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button size="lg" variant="secondary" className="shadow-lg" asChild>
                      <a
                        href="/book-now"
                        rel="noopener noreferrer"
                      >
                      <Calendar className="w-5 h-5 mr-2" />
                      Book an Appointment
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white hover:text-primary bg-transparent"
                    asChild
                  >
                    <Link to="/locations">
                      Find Our Locations
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
    </>
  )
}
